/**
 * MeasureController — AK-320 «Линейка на карте» v1.
 *
 * Собственная машина состояний рисования (НЕ mapbox-gl-draw / terra-draw /
 * MapDrawController). Архитектор (соблюдать строго):
 *   - state machine: idle | placing | done; + текущий mode
 *   - pointer-handlers: click / mousemove / dblclick / contextmenu / keydown(Esc)
 *   - аккумулируем координаты в state; на изменение — ОДИН setData(fc),
 *     throttle через requestAnimationFrame
 *   - live-preview = зафиксированные точки + temp-точка на курсоре
 *
 * Изоляция инвариантов карты:
 *   - свой GeoJSON source `measure-src` + слои measure-line / measure-fill
 *     (amber ~0.15) / measure-vertices, добавляются ПОСЛЕ активации,
 *     addLayer без beforeId (поверх). Префикс measure-.
 *   - НЕ трогаем registry.ts / LayerManager / setupMapLayers / mapReducer.
 *   - подписи = HTML-оверлей (maplibregl.Marker) ВНЕ collision-движка.
 *
 * Геодезия — точечные turf-пакеты через measure/geo.ts.
 * Цвет — accent-brand (amber), резолвится из computed CSS в рантайме
 * (MapLibre paint не понимает var()).
 */
import maplibregl from 'maplibre-gl';
import type { CircleMode, MeasureMode, MeasureResult } from './measureStore';
import {
  distanceMeters,
  pathLengthMeters,
  segmentLengthsMeters,
  polygonAreaSqm,
  polygonPerimeterMeters,
  circlePolygon,
  circleAreaSqm,
  formatDistance,
  formatArea,
  type LngLat,
} from './geo';
import { isMapAlive } from '../isMapAlive';

type Phase = 'idle' | 'placing' | 'done';

const SRC = 'measure-src';
const L_FILL = 'measure-fill';
const L_LINE = 'measure-line';
const L_VERTICES = 'measure-vertices';

/**
 * Радиус hit-test'а первой вершины (screen-px) для явного замыкания периметра
 * в режиме area: клик в этом радиусе от первой точки = finalize.
 */
const CLOSE_HIT_PX = 10;

/**
 * Amber accent-brand. MapLibre paint НЕ резолвит `var(--accent-brand)`,
 * поэтому читаем computed-значение токена в рантайме (как pillSprite в #359).
 * Фолбэк — задокументированная Earth Tech amber-константа (palette-accent-500),
 * на случай SSR/отсутствия DOM.
 */
const AMBER_FALLBACK = 'rgb(244, 163, 0)'; // Earth Tech --palette-accent-500 ≈ #F4A300
function resolveAmber(): string {
  if (typeof document === 'undefined') return AMBER_FALLBACK;
  const probe = document.createElement('span');
  probe.style.cssText = `position:absolute;left:-9999px;top:-9999px;color:hsl(var(--accent-brand));`;
  document.body.appendChild(probe);
  try {
    const c = getComputedStyle(probe).color;
    return c || AMBER_FALLBACK;
  } catch {
    return AMBER_FALLBACK;
  } finally {
    probe.remove();
  }
}

interface Label {
  marker: maplibregl.Marker;
  lngLat: LngLat;
}

export class MeasureController {
  private phase: Phase = 'idle';
  private mode: MeasureMode = 'line';
  /** Способ построения круга (center→edge | edge→edge). */
  private circleMode: CircleMode = 'center';
  /**
   * Курсор «висит» над первой вершиной (area, ≥3 точек) — «магнит» замыкания:
   * подсвечиваем первую точку и меняем курсор на pointer. true только в
   * phase=placing.
   */
  private hoverFirstVertex = false;
  /** Зафиксированные клик-точки. */
  private points: LngLat[] = [];
  /** Текущая позиция курсора (temp-точка) при phase=placing. */
  private cursor: LngLat | null = null;
  private rafPending = false;
  private installed = false;
  private labels: Label[] = [];
  private amber = AMBER_FALLBACK;
  /** Подавляем «двойную точку» от парного click при dblclick-финале. */
  private suppressNextClick = false;
  /** Экранная позиция последнего click'а (px) — для дедупа близких точек. */
  private lastClickPx: { x: number; y: number } | null = null;
  /** Порог дедупа (px): два click'а ближе этого = один (dblclick-парность). */
  private static readonly DEDUP_PX = 6;

  constructor(
    private map: maplibregl.Map,
    private onResult: (r: MeasureResult | null) => void,
  ) {}

  // ── lifecycle ──────────────────────────────────────────────────────────

  /** Включить тул: source+layers, слушатели, crosshair-курсор. */
  activate(mode: MeasureMode, circleMode: CircleMode = 'center'): void {
    this.amber = resolveAmber();
    this.mode = mode;
    this.circleMode = circleMode;
    this.reset();
    this.ensureLayers();
    if (!this.installed) {
      this.map.on('click', this.onClick);
      this.map.on('mousemove', this.onMouseMove);
      this.map.on('dblclick', this.onDblClick);
      this.map.on('contextmenu', this.onContextMenu);
      window.addEventListener('keydown', this.onKeyDown);
      // crosshair-курсор пока тул активен.
      this.map.getCanvas().style.cursor = 'crosshair';
      // dblclick рисует точку И зумит — нам зум не нужен при рисовании.
      this.map.doubleClickZoom.disable();
      this.installed = true;
    }
    this.phase = 'placing';
  }

  /** Сменить режим: очищает текущий рисунок, остаётся активным. */
  setMode(mode: MeasureMode): void {
    this.mode = mode;
    this.reset();
    this.phase = 'placing';
    this.amber = resolveAmber();
    this.repaintColors();
  }

  /** Сменить способ построения круга: очищает текущий рисунок. */
  setCircleMode(circleMode: CircleMode): void {
    this.circleMode = circleMode;
    this.reset();
    this.phase = 'placing';
  }

  /**
   * «Очистить» — стереть текущий рисунок/точки/подписи, остаться активным.
   * Отдельный публичный метод (root-cause бага: повтор того же mode не
   * триггерил setMode в подписке, поэтому оверлей не чистился).
   */
  clear(): void {
    this.reset();
    this.phase = 'placing';
    this.onResult(null);
  }

  /** «Готово» — финализировать текущее рисование (если возможно). */
  finalizeFromUi(): void {
    this.finalize();
  }

  /** Полностью выключить тул: снять слушатели, вернуть курсор, убрать оверлей. */
  deactivate(): void {
    if (this.installed) {
      this.map.off('click', this.onClick);
      this.map.off('mousemove', this.onMouseMove);
      this.map.off('dblclick', this.onDblClick);
      this.map.off('contextmenu', this.onContextMenu);
      window.removeEventListener('keydown', this.onKeyDown);
      this.map.getCanvas().style.cursor = '';
      this.map.doubleClickZoom.enable();
      this.installed = false;
    }
    this.reset();
    this.removeLayers();
    this.phase = 'idle';
    this.onResult(null);
  }

  // ── source / layers ──────────────────────────────────────────────────────

  private ensureLayers(): void {
    if (!this.map.getSource(SRC)) {
      this.map.addSource(SRC, {
        type: 'geojson',
        data: this.emptyFc(),
      });
    }
    // fill (площадь/круг) — amber ~0.15, под линией.
    if (!this.map.getLayer(L_FILL)) {
      this.map.addLayer({
        id: L_FILL,
        type: 'fill',
        source: SRC,
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: {
          'fill-color': this.amber,
          'fill-opacity': 0.15,
        },
      });
    }
    // line — amber, поверх заливки.
    if (!this.map.getLayer(L_LINE)) {
      this.map.addLayer({
        id: L_LINE,
        type: 'line',
        source: SRC,
        filter: ['!=', ['geometry-type'], 'Point'],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': this.amber,
          'line-width': 2.5,
        },
      });
    }
    // vertices — белые точки с amber-обводкой, самый верх. magnet=1 (первая
    // вершина area при наведении) — крупнее и залита amber («магнит» замыкания).
    if (!this.map.getLayer(L_VERTICES)) {
      this.map.addLayer({
        id: L_VERTICES,
        type: 'circle',
        source: SRC,
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-radius': ['case', ['==', ['get', 'magnet'], 1], 7, 4],
          'circle-color': ['case', ['==', ['get', 'magnet'], 1], this.amber, '#ffffff'],
          'circle-stroke-color': this.amber,
          'circle-stroke-width': ['case', ['==', ['get', 'magnet'], 1], 3, 2],
        },
      });
    }
  }

  private repaintColors(): void {
    if (this.map.getLayer(L_FILL)) this.map.setPaintProperty(L_FILL, 'fill-color', this.amber);
    if (this.map.getLayer(L_LINE)) this.map.setPaintProperty(L_LINE, 'line-color', this.amber);
    if (this.map.getLayer(L_VERTICES)) {
      this.map.setPaintProperty(L_VERTICES, 'circle-stroke-color', this.amber);
      this.map.setPaintProperty(L_VERTICES, 'circle-color', [
        'case',
        ['==', ['get', 'magnet'], 1],
        this.amber,
        '#ffffff',
      ]);
    }
  }

  private removeLayers(): void {
    // P0 (AK-343): deactivate() → removeLayers() can run during MapPageV2
    // unmount after `map.remove()` (nulled style). `getLayer`/`getSource` would
    // throw `Cannot read properties of null` → error boundary. remove() already
    // dropped all sources/layers, so bail cleanly.
    if (!isMapAlive(this.map)) return;
    for (const id of [L_VERTICES, L_LINE, L_FILL]) {
      if (this.map.getLayer(id)) this.map.removeLayer(id);
    }
    if (this.map.getSource(SRC)) this.map.removeSource(SRC);
  }

  // ── pointer handlers ──────────────────────────────────────────────────────

  private onClick = (e: maplibregl.MapMouseEvent): void => {
    if (this.suppressNextClick) {
      this.suppressNextClick = false;
      return;
    }
    const ll: LngLat = [e.lngLat.lng, e.lngLat.lat];

    if (this.phase === 'done') {
      // Новый замер — начинаем заново с этой точки.
      this.reset();
      this.phase = 'placing';
    }

    // Явное замыкание периметра (area): клик по ПЕРВОЙ вершине (hit-test в
    // CLOSE_HIT_PX screen-space) при ≥3 точках = finalize. Делаем ДО дедупа и
    // push — иначе замыкающий клик добавился бы лишней точкой.
    if (this.mode === 'area' && this.points.length >= 3 && this.isNearFirstVertex(e.point)) {
      this.hoverFirstVertex = false;
      this.finalize();
      return;
    }

    // Дедуп: для ломаной/фигуры два click'а в одном пикселе (первый из пары
    // dblclick'а) НЕ должны давать дубль-точку с нулевым сегментом. Сравниваем
    // по screen-px, т.к. lngLat у пары идентичны.
    if (
      (this.mode === 'path' || this.mode === 'area') &&
      this.lastClickPx &&
      e.point &&
      Math.hypot(e.point.x - this.lastClickPx.x, e.point.y - this.lastClickPx.y) <
        MeasureController.DEDUP_PX &&
      this.points.length > 0
    ) {
      this.lastClickPx = { x: e.point.x, y: e.point.y };
      return;
    }
    if (e.point) this.lastClickPx = { x: e.point.x, y: e.point.y };

    switch (this.mode) {
      case 'line':
        this.points.push(ll);
        if (this.points.length === 2) {
          this.phase = 'done';
          this.cursor = null;
        }
        break;
      case 'circle':
        if (this.points.length === 0) {
          this.points.push(ll); // центр
        } else {
          this.points[1] = ll; // точка радиуса → финал
          this.phase = 'done';
          this.cursor = null;
        }
        break;
      case 'path':
      case 'area':
        this.points.push(ll);
        break;
    }
    this.scheduleRender();
  };

  private onMouseMove = (e: maplibregl.MapMouseEvent): void => {
    if (this.phase !== 'placing') return;
    // framePoints() сам решает, рисовать ли temp-точку (до первой клик-точки
    // её не показываем для path/area).
    this.cursor = [e.lngLat.lng, e.lngLat.lat];
    // «Магнит» замыкания (area, ≥3 точек): подсветка первой вершины + pointer-
    // курсор, когда курсор над ней. Иначе — crosshair.
    const wasHover = this.hoverFirstVertex;
    this.hoverFirstVertex =
      this.mode === 'area' && this.points.length >= 3 && this.isNearFirstVertex(e.point);
    if (this.hoverFirstVertex !== wasHover) {
      this.map.getCanvas().style.cursor = this.hoverFirstVertex ? 'pointer' : 'crosshair';
    }
    this.scheduleRender();
  };

  private onDblClick = (e: maplibregl.MapMouseEvent): void => {
    // Финал ломаной/фигуры. Подавляем парный click, который бы добавил
    // дубль-точку (debounce: dblclick всегда идёт после второго click).
    if (this.mode !== 'path' && this.mode !== 'area') return;
    e.preventDefault();
    this.suppressNextClick = true;
    // Последний mousemove мог не успеть зафиксировать точку — финализируем по
    // тем, что есть (dblclick = два click'а уже добавили точки).
    this.finalize();
  };

  private onContextMenu = (e: maplibregl.MapMouseEvent): void => {
    // ПКМ — отменить последнюю точку (или весь замер если осталась одна).
    if (this.phase !== 'placing') return;
    e.preventDefault();
    if (this.points.length > 0) this.points.pop();
    this.scheduleRender();
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') return;
    if (this.phase === 'placing' && this.canFinalize()) {
      this.finalize(); // Esc как финал ломаной/фигуры
    } else {
      // Esc как отмена текущего рисунка.
      this.reset();
      this.phase = 'placing';
      this.scheduleRender();
      this.onResult(null);
    }
  };

  // ── finalize / measure ──────────────────────────────────────────────────

  private canFinalize(): boolean {
    if (this.mode === 'path') return this.points.length >= 2;
    if (this.mode === 'area') return this.points.length >= 3;
    // line / circle авто-финализируются на 2-й точке; «Готово» допустимо, если
    // обе точки уже стоят (защита от финала с одной точкой).
    return this.points.length >= 2;
  }

  private finalize(): void {
    if (this.phase === 'done') return;
    if (!this.canFinalize()) return;
    this.phase = 'done';
    this.cursor = null;
    this.hoverFirstVertex = false;
    this.scheduleRender();
  }

  // ── render (rAF-throttled) ─────────────────────────────────────────────────

  private scheduleRender(): void {
    if (this.rafPending) return;
    this.rafPending = true;
    requestAnimationFrame(() => {
      this.rafPending = false;
      this.render();
    });
  }

  /** Точки для текущего кадра: зафиксированные + temp-курсор (live-preview). */
  private framePoints(): LngLat[] {
    if (this.phase === 'placing' && this.cursor) {
      // line: показываем превью второго конца. path/area: превью след. сегмента.
      if (this.mode === 'line' && this.points.length === 1) {
        return [...this.points, this.cursor];
      }
      if ((this.mode === 'path' || this.mode === 'area') && this.points.length >= 1) {
        return [...this.points, this.cursor];
      }
      if (this.mode === 'circle' && this.points.length === 1) {
        return [...this.points, this.cursor];
      }
    }
    return [...this.points];
  }

  private render(): void {
    const src = this.map.getSource(SRC) as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    const pts = this.framePoints();
    const features: GeoJSON.Feature[] = [];

    let result: MeasureResult | null = null;

    if (this.mode === 'circle') {
      const p0 = this.points[0];
      const p1 = pts[1];
      if (p0 && p1) {
        // center-режим: p0 = центр, p1 = край. diameter-режим: p0/p1 = два
        // противоположных края, центр = середина, радиус = ½ расстояния.
        const center: LngLat =
          this.circleMode === 'diameter' ? this.midpoint(p0, p1) : p0;
        const r =
          this.circleMode === 'diameter'
            ? distanceMeters(p0, p1) / 2
            : distanceMeters(center, p1);
        const ring = circlePolygon(center, r, 64);
        if (ring.length >= 4) {
          features.push(this.poly(ring));
        }
        if (this.circleMode === 'diameter') {
          // линия-диаметр (край→край) + обе клик-точки на окружности.
          features.push(this.line([p0, p1]));
          features.push(this.point(p0));
          if (this.phase === 'done') features.push(this.point(p1));
        } else {
          // линия-радиус (центр→край) + центр.
          features.push(this.line([center, p1]));
          features.push(this.point(center));
          if (this.phase === 'done') features.push(this.point(p1));
        }
        const areaSqm = circleAreaSqm(center, r, 64);
        const ap = formatArea(areaSqm);
        result = {
          mode: 'circle',
          primary: ap.primary,
          secondary: `R ${formatDistance(r)}${ap.secondary ? ` · ${ap.secondary}` : ''}`,
          raw: { areaSqm, radiusMeters: r },
        };
        this.placeLabel(this.midpoint(center, p1), ap.primary);
      } else if (p0) {
        features.push(this.point(p0));
        this.clearLabels();
      }
    } else if (this.mode === 'area') {
      if (pts.length >= 1) {
        // Первая вершина получает magnet=1 при наведении (подсветка-«магнит»
        // замыкания); cursor-temp-точка (последняя в pts при placing) — обычная.
        features.push(
          ...pts.map((p, i) =>
            this.point(p, i === 0 && this.hoverFirstVertex ? { magnet: 1 } : {}),
          ),
        );
      }
      if (pts.length >= 2) {
        const ring = this.phase === 'done' ? this.closed(pts) : pts;
        if (this.phase === 'done' && pts.length >= 3) {
          features.push(this.poly(this.closed(pts)));
        }
        features.push(this.line(ring));
      }
      if (pts.length >= 3) {
        const areaSqm = polygonAreaSqm(pts);
        const perim = polygonPerimeterMeters(pts);
        const ap = formatArea(areaSqm);
        result = {
          mode: 'area',
          primary: ap.primary,
          secondary: `Периметр ${formatDistance(perim)}${ap.secondary ? ` · ${ap.secondary}` : ''}`,
          raw: { areaSqm, perimeterMeters: perim },
        };
        this.placeLabel(this.centroid(pts), ap.primary);
      } else {
        this.clearLabels();
      }
    } else {
      // line / path
      if (pts.length >= 1) features.push(...pts.map((p) => this.point(p)));
      if (pts.length >= 2) features.push(this.line(pts));
      if (pts.length >= 2) {
        const total = pathLengthMeters(pts);
        const segs = segmentLengthsMeters(pts);
        result = {
          mode: this.mode,
          primary: formatDistance(total),
          secondary:
            this.mode === 'path' && segs.length > 1
              ? `${segs.length} сегм.`
              : undefined,
          raw: { distanceMeters: total, segmentsMeters: segs },
        };
        this.placeLabel(pts[pts.length - 1], formatDistance(total));
      } else {
        this.clearLabels();
      }
    }

    src.setData({ type: 'FeatureCollection', features });
    this.onResult(result);
  }

  // ── feature helpers ────────────────────────────────────────────────────

  private emptyFc(): GeoJSON.FeatureCollection {
    return { type: 'FeatureCollection', features: [] };
  }
  private point(c: LngLat, props: GeoJSON.GeoJsonProperties = {}): GeoJSON.Feature {
    return { type: 'Feature', properties: props, geometry: { type: 'Point', coordinates: c } };
  }
  private line(cs: LngLat[]): GeoJSON.Feature {
    return { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: cs } };
  }
  private poly(ring: LngLat[]): GeoJSON.Feature {
    return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [ring] } };
  }
  private closed(cs: LngLat[]): LngLat[] {
    const a = cs[0];
    const b = cs[cs.length - 1];
    return a[0] === b[0] && a[1] === b[1] ? cs : [...cs, a];
  }

  // ── labels (HTML-оверлей вне collision-движка) ─────────────────────────────

  private placeLabel(at: LngLat, text: string): void {
    this.clearLabels();
    const el = document.createElement('div');
    el.className = 'measure-label';
    el.textContent = text;
    // Earth Tech: белая пилюля, моно-цифры (tabular-nums). Inline-стили —
    // overlay вне Tailwind-scope; цвет нейтральный (не amber hex в whitelist).
    el.style.cssText = [
      'background:rgba(255,255,255,0.96)',
      'color:#1c1917',
      'border:1px solid rgba(231,229,228,0.9)',
      'border-radius:9999px',
      'padding:2px 8px',
      'font-size:12px',
      'font-weight:600',
      'font-variant-numeric:tabular-nums',
      'box-shadow:0 1px 3px rgba(0,0,0,0.18)',
      'white-space:nowrap',
      'pointer-events:none',
      'transform:translateY(-4px)',
    ].join(';');
    const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
      .setLngLat(at)
      .addTo(this.map);
    this.labels.push({ marker, lngLat: at });
  }

  private clearLabels(): void {
    for (const l of this.labels) l.marker.remove();
    this.labels = [];
  }

  // ── geometry utils ─────────────────────────────────────────────────────

  private midpoint(a: LngLat, b: LngLat): LngLat {
    return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  }

  /**
   * Курсор-пиксель близок к первой вершине (≤CLOSE_HIT_PX)? Проецируем
   * геокоординату первой точки в screen-space через map.project и сравниваем
   * с пиксельной позицией события. map.project может отсутствовать в тест-моке
   * — тогда hit-test выключен (false).
   */
  private isNearFirstVertex(pt: { x: number; y: number } | undefined): boolean {
    if (!pt || this.points.length === 0) return false;
    const project = (this.map as { project?: (l: LngLat) => { x: number; y: number } }).project;
    if (typeof project !== 'function') return false;
    const first = project.call(this.map, this.points[0]);
    return Math.hypot(pt.x - first.x, pt.y - first.y) <= CLOSE_HIT_PX;
  }
  private centroid(cs: LngLat[]): LngLat {
    let x = 0;
    let y = 0;
    for (const c of cs) {
      x += c[0];
      y += c[1];
    }
    return [x / cs.length, y / cs.length];
  }

  private reset(): void {
    this.points = [];
    this.cursor = null;
    this.lastClickPx = null;
    this.hoverFirstVertex = false;
    if (this.installed) this.map.getCanvas().style.cursor = 'crosshair';
    this.clearLabels();
    // P0 (AK-343): reset() is called from deactivate() during unmount teardown,
    // which can run after `map.remove()` (nulled style). getSource() would throw
    // on the dead map → error boundary. Bail — there is no source to clear.
    if (!isMapAlive(this.map)) return;
    const src = this.map.getSource(SRC) as maplibregl.GeoJSONSource | undefined;
    if (src) src.setData(this.emptyFc());
  }
}
